import { supabase } from '@/integrations/supabase/client';
import { prepareImageForUpload } from '@/lib/imageUtils';

export const TEMP_ID_PREFIX = '__temp__';

/** Generate a unique temporary ID for optimistic messages */
export function makeTempId(): string {
  return `${TEMP_ID_PREFIX}${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

/** Check if an ID is a temporary optimistic ID */
export function isTempId(id: string): boolean {
  return id.startsWith(TEMP_ID_PREFIX);
}

/** Scroll a container ref to its bottom using requestAnimationFrame */
export function scrollToBottom(ref: React.RefObject<HTMLDivElement>) {
  requestAnimationFrame(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  });
}

/** Upload and compress a chat image to a given storage bucket */
export async function uploadChatImage(
  file: File,
  bucket: string,
  pathPrefix: string,
): Promise<string> {
  let blob: Blob;
  let extension: string;
  try {
    const result = await prepareImageForUpload(file, 'chatAttachment');
    blob = result.blob;
    extension = result.extension;
  } catch (err: any) {
    // Surface compression failures (e.g., empty/black canvas on iOS) as a friendly error
    throw new Error(err?.message || 'Não foi possível processar a imagem — tente enviar em outro formato.');
  }
  const path = `${pathPrefix}/${Date.now()}.${extension}`;
  const { error } = await supabase.storage.from(bucket).upload(path, blob, { contentType: 'image/webp' });
  if (error) throw error;
  // For private buckets (e.g. support-images) getPublicUrl still returns a parseable URL;
  // the UI resolves it to a private blob URL via useSignedSupportImageUrl.
  return supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
}

/**
 * Deduplicate a Realtime INSERT for the current user's own messages.
 * If a temp message exists matching the incoming real message, replaces it.
 * Returns true if deduplication happened (caller should skip normal append).
 */
export function deduplicateRealtimeInsert<T extends { id: string; user_id?: string; sender_id?: string; content?: string }>(
  pendingTempIds: Set<string>,
  newMsg: T,
  currentUserId: string,
  messages: T[],
): { deduplicated: boolean; updatedMessages: T[] } {
  const senderId = newMsg.user_id ?? (newMsg as any).sender_id;
  if (senderId !== currentUserId || pendingTempIds.size === 0) {
    return { deduplicated: false, updatedMessages: messages };
  }

  // Already have the real message
  if (messages.some(m => m.id === newMsg.id)) {
    return { deduplicated: true, updatedMessages: messages };
  }

  // Find matching temp message
  const tempIdx = messages.findIndex(m =>
    isTempId(m.id) &&
    (m.user_id ?? (m as any).sender_id) === senderId &&
    (m.content ?? '') === (newMsg.content ?? '')
  );

  if (tempIdx !== -1) {
    const tempId = messages[tempIdx].id;
    pendingTempIds.delete(tempId);
    const updated = [...messages];
    updated[tempIdx] = { ...updated[tempIdx], ...newMsg };
    return { deduplicated: true, updatedMessages: updated };
  }

  return { deduplicated: false, updatedMessages: messages };
}
